import React, { useCallback, useEffect, useState } from 'react';
import {
  getNotificationPrefs,
  saveNotificationPrefs,
  sendTestNotification,
  getTelegramStatus,
  startTelegramLink,
  unlinkTelegram,
  CHANNEL_LABELS,
  EVENT_LABELS,
} from './signalDataFeeds';

// Account-wide alert settings.
//
// These used to live on each signal's Alerts panel, which meant a Discord webhook had to be
// pasted again for every new signal — and the test button during signal setup had nowhere to
// read a webhook from at all, so testing Discord was impossible before the signal existed.
//
// Contact details belong to the ACCOUNT, not to a signal: one Telegram chat, one Discord
// webhook, one email address. Per-signal settings now only choose which events matter and
// whether that signal is muted.
//
// Written to the deploymentId 0 preference row, which the notifier already treats as the
// account-wide default and falls back to for every signal.

// All five event types the server accepts (NOTIFY_EVENT_TYPES in signalService.js). The
// per-signal panel offers only the three trade events; drift and milestone alerts are about the
// signal's health rather than an individual trade, so they belong at the account level — and
// leaving them out here would make them unreachable settings that are nonetheless switched on
// by default.
const NOTIFY_OPTIONS = ['trade_open', 'trade_close', 'position_update', 'drift_alert', 'milestone'];

function AlertSettingsPanel({ user }) {
  const [prefs, setPrefs] = useState(null);
  const [telegram, setTelegram] = useState(null);
  const [webhookDraft, setWebhookDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [testing, setTesting] = useState('');
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    const [loaded, tg] = await Promise.all([
      getNotificationPrefs(0).catch(() => null),
      getTelegramStatus().catch(() => null),
    ]);
    if (loaded) {
      setPrefs(loaded);
      setWebhookDraft(loaded.discordWebhookUrl || '');
    }
    setTelegram(tg);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const persist = async (patch) => {
    if (!prefs) return;
    const next = { ...prefs, ...patch };
    setPrefs(next);
    setSaving(true);
    setError('');
    setNotice('');
    try {
      await saveNotificationPrefs({
        deploymentId: 0,
        channels: next.channels,
        notifyOn: next.notifyOn,
        muted: next.muted,
        discordWebhookUrl: next.discordWebhookUrl || '',
      });
    } catch (err) {
      setError(err.message);
      await refresh();
    } finally {
      setSaving(false);
    }
  };

  const toggleIn = (list, value) => (
    (list || []).includes(value) ? list.filter((item) => item !== value) : [...(list || []), value]
  );

  const runTest = async (channel) => {
    setTesting(channel);
    setError('');
    setNotice('');
    try {
      await sendTestNotification(channel);
      setNotice(channel === 'inapp'
        ? 'Test sent — it should appear under Recent alerts below.'
        : `Test sent to ${CHANNEL_LABELS[channel] || channel}.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setTesting('');
    }
  };

  const linkTelegram = async () => {
    setError('');
    try {
      const result = await startTelegramLink();
      // The route returns `url` (a t.me deep link carrying a one-time nonce).
      if (result.url) window.open(result.url, '_blank', 'noopener');
      setNotice('Press Start in Telegram, then reopen this panel to confirm it linked.');
    } catch (err) {
      setError(err.message);
    }
  };

  if (!prefs) return null;

  const channels = prefs.channels || [];

  return (
    <div className="signal-collapse">
      <button
        type="button"
        className="signal-collapse-toggle"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span>Alert settings {saving && <small className="muted-text">saving…</small>}</span>
        <span className="signal-collapse-summary">
          {channels.length ? channels.map((c) => CHANNEL_LABELS[c] || c).join(' · ') : 'No channels on'}
          <em>{open ? '▲' : '▼'}</em>
        </span>
      </button>

      {open && (
        <div className="signal-collapse-body">
          <p className="muted-text">
            Set these once — every signal you deploy or follow uses them. Individual signals can
            still choose which events they alert on.
          </p>

          {notice && <p className="success-text">{notice}</p>}
          {error && <p className="signal-error">{error}</p>}

          {/* ── Channels ─────────────────────────────────────────────────── */}
          <h5>Where alerts go</h5>
          <div className="signal-channel-rows">
            {Object.entries(CHANNEL_LABELS).map(([channel, label]) => (
              <div className="signal-channel-row" key={channel}>
                <label className="signal-toggle">
                  <input
                    type="checkbox"
                    checked={channels.includes(channel)}
                    onChange={() => persist({ channels: toggleIn(channels, channel) })}
                  />
                  <span>{label}</span>
                </label>

                <div className="signal-channel-detail">
                  {channel === 'email' && (
                    <span className="muted-text">{user?.email || 'your account email'}</span>
                  )}
                  {channel === 'inapp' && (
                    <span className="muted-text">Shown under Recent alerts</span>
                  )}
                  {channel === 'telegram' && (
                    // `configured` is false when the server has no VIX_TELEGRAM_BOT_USERNAME —
                    // there is no bot to press Start on, so offering a Link button would just
                    // produce a dead end.
                    !telegram?.configured ? (
                      <span className="muted-text">Not available yet — no bot configured on the server</span>
                    ) : telegram?.linked ? (
                      <>
                        <span className="muted-text">
                          Linked{telegram.username ? ` as @${telegram.username}` : ''}
                        </span>
                        <button
                          type="button"
                          className="ghost-button compact-button"
                          onClick={async () => { await unlinkTelegram(); refresh(); }}
                        >
                          Unlink
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="muted-text">Not linked</span>
                        <button type="button" className="ghost-button compact-button" onClick={linkTelegram}>
                          Link Telegram
                        </button>
                      </>
                    )
                  )}
                  {channel === 'discord' && (
                    <span className="muted-text">
                      {prefs.discordWebhookUrl ? 'Webhook saved' : 'Needs a webhook URL'}
                    </span>
                  )}
                </div>

                <button
                  type="button"
                  className="ghost-button compact-button"
                  disabled={testing === channel}
                  onClick={() => runTest(channel)}
                >
                  {testing === channel ? 'Sending…' : 'Test'}
                </button>
              </div>
            ))}
          </div>

          {/* Only shown when Discord is on — an unexplained webhook field is just confusing. */}
          {channels.includes('discord') && (
            <label className="signal-field signal-webhook-field">
              <span>Discord webhook URL</span>
              <input
                type="url"
                placeholder="https://discord.com/api/webhooks/..."
                value={webhookDraft}
                onChange={(event) => setWebhookDraft(event.target.value)}
                onBlur={() => {
                  const trimmed = webhookDraft.trim();
                  if (trimmed !== (prefs.discordWebhookUrl || '')) {
                    persist({ discordWebhookUrl: trimmed });
                  }
                }}
              />
              <small className="muted-text">
                In Discord: Server Settings → Integrations → Webhooks → New Webhook, pick a
                channel, then Copy Webhook URL. Saved when you click away. This is your own
                private webhook — nobody else can post to it.
              </small>
            </label>
          )}

          {/* ── Default events ───────────────────────────────────────────── */}
          <h5>What to alert on by default</h5>
          <div className="signal-toggle-list">
            {NOTIFY_OPTIONS.map((type) => (
              <label key={type} className="signal-toggle">
                <input
                  type="checkbox"
                  checked={(prefs.notifyOn || []).includes(type)}
                  onChange={() => persist({ notifyOn: toggleIn(prefs.notifyOn, type) })}
                />
                <span>{EVENT_LABELS[type] || type}</span>
              </label>
            ))}
          </div>

          <label className="signal-toggle signal-mute-all">
            <input
              type="checkbox"
              checked={!!prefs.muted}
              onChange={() => persist({ muted: !prefs.muted })}
            />
            <span>Mute everything</span>
          </label>
        </div>
      )}
    </div>
  );
}

export default AlertSettingsPanel;
